import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import useMediaQuery from '@mui/material/useMediaQuery';
import AppBar from '@mui/material/AppBar';
import Link from '@mui/material/Link';
import ListItem from '@mui/material/ListItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FolderIcon from '@mui/icons-material/Folder';
import FolderIconOutlined from '@mui/icons-material/FolderOutlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFileOutlined';

import { useTheme } from '@mui/material';
import { green, blueGrey, red, deepOrange } from '@mui/material/colors';
import sortBy from 'lodash/sortBy';
import moment from 'moment';
import Spacings from '~/modules/ui-kit/spacings';
import HasRole, { useHasRole } from '~/containers/HasRole';
import ContentView from '~/components/ContentView';
import ConfirmationModal from '~/components/ConfirmationModal';
import ActionSelectionButton from '~/components/ActionSelectionButton';
import MultiFab from '~/components/MultiFab';
import DrawerButton from '~/containers/DrawerButton';
import Tag from '~/components/Tag';
import { useFetchFolderContent } from '~/hooks/api/useFetchFolderContent';
import { useQueryClient } from '@tanstack/react-query';
import { useDeleteFolder } from '~/hooks/api/useDeleteFolder';
import { useDeleteDocument } from '~/hooks/api/useDeleteDocument';
import { useRenameFolder } from '~/hooks/api/useRenameFolder';
import { useRenameDocument } from '~/hooks/api/useRenameDocument';
import RenameDocumentDialog from './RenameDocumentDialog';
import messages from './messages';
import { FolderPurpose } from '~/utils/folder-purpose';
import { FolderContentFetched, FolderDto, DocumentDto } from '~/generated';

const route = getRouteApi('/documents/$folderId/');

const ROOT_FOLDER_ID = 'root';

const serializeFolderId = (folderId: string | null | undefined): string => {
  if (folderId == null) {
    return ROOT_FOLDER_ID;
  }

  const serialized = String(folderId).trim().toLowerCase();
  if (serialized === '' || serialized === 'root') {
    return ROOT_FOLDER_ID;
  }

  return String(folderId);
};

const isRootFolderId = (folderId: string | null | undefined) =>
  serializeFolderId(folderId) === ROOT_FOLDER_ID;

const tagColors = {
  [FolderPurpose.UploadTeamLeader]: {
    backgroundColor: green[50],
    color: green[900],
  },
  [FolderPurpose.TeamLeaderInformation]: {
    backgroundColor: red[50],
    color: red[900],
  },
  [FolderPurpose.VipInformation]: {
    backgroundColor: red[50],
    color: red[900],
  },
  [FolderPurpose.PublicInformation]: {
    backgroundColor: blueGrey[50],
    color: blueGrey[900],
  },
  [FolderPurpose.CoordinationTeamExchange]: {
    backgroundColor: deepOrange[50],
    color: deepOrange[900],
  },
};

type PurposeTagProps = {
  purpose: FolderPurpose;
};

const PurposeTag = (props: PurposeTagProps) => {
  const intl = useIntl();
  const messageKey =
    `purpose${FolderPurpose[props.purpose]}` as keyof typeof messages;
  return (
    <Tag style={tagColors[props.purpose as keyof typeof tagColors]}>
      {intl.formatMessage(messages[messageKey])}
    </Tag>
  );
};

type DocumentListRowProps = {
  showExpiration: boolean;
  defaultAction?: (() => void) | null;
  rowHeight?: 'medium' | 'small';
  typeIcon?: React.ReactNode;
  elementName?: React.ReactNode;
  createdOn?: React.ReactNode;
  author?: React.ReactNode;
  purpose?: React.ReactNode;
  expiresOn?: React.ReactNode;
  actions?: React.ReactNode;
};

const DocumentListRow = (props: DocumentListRowProps) => {
  const min25em = useMediaQuery('(min-width: 25em)');
  const min40em = useMediaQuery('(min-width: 40em)');
  const min50em = useMediaQuery('(min-width: 50em)');
  const min70em = useMediaQuery('(min-width: 70em)');
  const hasDefaultAction = props.defaultAction != null;

  const execDefaultAction = () => {
    if (hasDefaultAction) {
      props.defaultAction!();
    }
  };

  return (
    <TableRow hover={hasDefaultAction} onClick={() => execDefaultAction()}>
      <TableCell size={props.rowHeight}>{props.typeIcon}</TableCell>
      <TableCell>{props.elementName}</TableCell>
      {min25em && (
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{props.createdOn}</TableCell>
      )}
      {min50em && (
        <HasRole roles={['Administrator']}>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>{props.author}</TableCell>
        </HasRole>
      )}
      {props.showExpiration
        ? min70em && (
            <HasRole roles={['Administrator']}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {props.expiresOn}
              </TableCell>
            </HasRole>
          )
        : null}
      {min40em && (
        <HasRole roles={['Administrator', 'ShiftLeader', 'Vip']}>
          <TableCell>{props.purpose}</TableCell>
        </HasRole>
      )}
      <HasRole roles={['Administrator', 'ShiftLeader']}>
        <TableCell align="right">{props.actions}</TableCell>
      </HasRole>
    </TableRow>
  );
};

const DocumentList = () => {
  const [folderToDelete, setFolderToDelete] = useState<FolderDto | null>(null);
  const [folderToRename, setFolderToRename] = useState<FolderDto | null>(null);
  const [documentToDelete, setDocumentToDelete] = useState<DocumentDto | null>(
    null
  );
  const [documentToRename, setDocumentToRename] = useState<DocumentDto | null>(
    null
  );

  const navigate = useNavigate();
  const params = route.useParams();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const { data: fetched, isLoading } = useFetchFolderContent(params.folderId);
  const { data: fullFolderToDelete } = useFetchFolderContent(
    serializeFolderId(folderToDelete?.id)
  );

  const intl = useIntl();
  const { showSnackbar } = useSnackbar();

  const invalidateFolderQuery = () => {
    queryClient.invalidateQueries({
      queryKey: ['document-share', 'folders'],
    });
  };

  const deleteFolderMutation = useDeleteFolder({
    onSuccess: () => {
      showSnackbar({
        message: intl.formatMessage(messages.deleteFolderSuccess),
      });
      setFolderToDelete(null);
      invalidateFolderQuery();
    },
    onError: () => {
      showSnackbar({
        message: intl.formatMessage(messages.deleteFolderFailure),
      });
      setFolderToDelete(null);
    },
  });

  const deleteDocumentMutation = useDeleteDocument({
    onSuccess: () => {
      showSnackbar({
        message: intl.formatMessage(messages.deleteDocumentSuccess),
      });
      setDocumentToDelete(null);
      invalidateFolderQuery();
    },
    onError: () => {
      showSnackbar({
        message: intl.formatMessage(messages.deleteDocumentFailure),
      });
      setDocumentToDelete(null);
    },
  });

  const renameFolderMutation = useRenameFolder({
    onSuccess: () => {
      showSnackbar({
        message: intl.formatMessage(messages.renameFolderDialogSuccess),
      });
      setFolderToRename(null);
      invalidateFolderQuery();
    },
    onError: () => {
      showSnackbar({
        message: intl.formatMessage(messages.renameFolderDialogFailure),
      });
      setFolderToRename(null);
    },
  });

  const renameDocumentMutation = useRenameDocument({
    onSuccess: () => {
      showSnackbar({
        message: intl.formatMessage(messages.renameDocumentDialogSuccess),
      });
      setDocumentToRename(null);
      invalidateFolderQuery();
    },
    onError: () => {
      showSnackbar({
        message: intl.formatMessage(messages.renameDocumentDialogFailure),
      });
      setDocumentToRename(null);
    },
  });

  const hasAnyElements = (fetched: FolderContentFetched) => {
    if (!fetched) return false;
    return (
      (fetched.content?.subFolders?.length ?? 0) +
        (fetched.content?.documents?.length ?? 0) >
      0
    );
  };

  const openFolder = (folderId: string) => {
    navigate({
      to: '/documents/$folderId',
      params: { folderId: serializeFolderId(folderId) },
    });
  };

  const openInNewTab = (url: string) => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
  };

  const isAdministrator = useHasRole(['Administrator']);
  const isTurnLeader = useHasRole(['ShiftLeader']);
  const showExpiration =
    !isLoading &&
    fetched?.folder?.contentExpiration &&
    fetched.folder.contentExpiration > 0
      ? true
      : false;
  const contentExpiresInDays = fetched?.folder?.contentExpiration ?? 999;
  const canModifyDocuments =
    isAdministrator ||
    (isTurnLeader &&
      !isLoading &&
      fetched?.folder?.purpose === FolderPurpose.UploadTeamLeader);

  const getFabActions = (folderId: string) =>
    [
      {
        icon: <InsertDriveFileIcon />,
        name: intl.formatMessage(messages.createDocument),
        onClick: () =>
          navigate({
            to: '/documents/$folderId/newDocument',
            params: { folderId: folderId },
            search: { from: `/documents/${folderId}` },
          }),
        isAuthorized: canModifyDocuments,
      },
      {
        icon: <FolderIconOutlined />,
        name: intl.formatMessage(messages.createFolder),
        onClick: () =>
          navigate({
            to: '/documents/$folderId/newFolder',
            params: { folderId: folderId },
            search: { from: `/documents/${folderId}` },
          }),
        isAuthorized: isAdministrator,
      },
    ].filter((a) => a.isAuthorized);

  const getFolderActions = (folder: FolderDto) =>
    isAdministrator
      ? [
          {
            name: intl.formatMessage(messages.deleteFolder),
            ariaLabel: intl.formatMessage(messages.deleteAction, {
              name: folder.name,
            }),
            onClick: () => setFolderToDelete(folder),
          },
          {
            name: intl.formatMessage(messages.renameFolder),
            ariaLabel: intl.formatMessage(messages.renameAction, {
              name: folder.name,
            }),
            onClick: () => setFolderToRename(folder),
          },
        ]
      : [];

  const getDocumentActions = (document: DocumentDto) =>
    canModifyDocuments
      ? [
          {
            name: intl.formatMessage(messages.deleteDocument),
            ariaLabel: intl.formatMessage(messages.deleteAction, {
              name: document.name,
            }),
            onClick: () => setDocumentToDelete(document),
          },
          {
            name: intl.formatMessage(messages.renameDocument),
            ariaLabel: intl.formatMessage(messages.renameAction, {
              name: document.name,
            }),
            onClick: () => setDocumentToRename(document),
          },
        ]
      : [];

  return (
    <>
      <ContentView
        header={
          <div>
            <AppBar position="static">
              <Toolbar>
                <DrawerButton />
                <Typography
                  variant="h6"
                  style={{ marginLeft: theme.spacing(2) }}
                  sx={{
                    color: 'inherit',
                  }}
                >
                  {}
                  {(() => {
                    if (
                      isLoading ||
                      !fetched ||
                      isRootFolderId(fetched?.folder?.id)
                    ) {
                      return <FormattedMessage {...messages.title} />;
                    }
                    if (
                      fetched.folder?.purpose ===
                      FolderPurpose.PublicInformation
                    ) {
                      return (
                        <FormattedMessage
                          {...messages.titleWithName}
                          values={{
                            name: fetched.folder?.name,
                          }}
                        />
                      );
                    }
                    return (
                      <Spacings.Inline scale="m">
                        <div>
                          <FormattedMessage
                            {...messages.titleWithName}
                            values={{
                              name: fetched.folder?.name,
                            }}
                          />
                        </div>
                        <div>
                          <PurposeTag purpose={fetched.folder?.purpose ?? 0} />
                        </div>
                      </Spacings.Inline>
                    );
                  })()}
                </Typography>
              </Toolbar>
            </AppBar>
          </div>
        }
        isLoading={isLoading}
        floatingActionButton={
          isLoading ? null : (
            <MultiFab
              ariaLabel={intl.formatMessage(messages.createDocumentOrFolder)}
              icon={<AddIcon />}
              actions={getFabActions(serializeFolderId(fetched?.folder?.id))}
              data-testid="create-document-or-folder"
            />
          )
        }
      >
        {!isLoading &&
          fetched &&
          (hasAnyElements(fetched) ? (
            <TableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <DocumentListRow
                    rowHeight="medium"
                    typeIcon={<FormattedMessage {...messages.colHeaderType} />}
                    showExpiration={showExpiration}
                    elementName={
                      <FormattedMessage {...messages.colHeaderName} />
                    }
                    createdOn={
                      <FormattedMessage {...messages.colHeaderLastModified} />
                    }
                    author={<FormattedMessage {...messages.colHeaderAuthor} />}
                    purpose={
                      <FormattedMessage {...messages.colHeaderPurpose} />
                    }
                    expiresOn={
                      <FormattedMessage {...messages.colHeaderDeleted} />
                    }
                    actions={
                      <FormattedMessage {...messages.colHeaderActions} />
                    }
                  />
                </TableHead>
                <TableBody>
                  {isRootFolderId(fetched.folder?.id) ? null : (
                    <DocumentListRow
                      key={fetched.folder?.parentFolderId}
                      defaultAction={() =>
                        fetched.folder?.parentFolderId != null
                          ? openFolder(
                              serializeFolderId(fetched.folder.parentFolderId)
                            )
                          : undefined
                      }
                      typeIcon={<ExpandLessIcon />}
                      showExpiration={showExpiration}
                      elementName={
                        <FormattedMessage {...messages.gotoParentFolder} />
                      }
                    />
                  )}
                  {sortBy(fetched.content?.subFolders ?? [], 'name').map(
                    (folder) => {
                      const dirActions = getFolderActions(folder);
                      return (
                        <DocumentListRow
                          key={folder.id}
                          defaultAction={() =>
                            folder.id != null
                              ? openFolder(serializeFolderId(folder.id))
                              : undefined
                          }
                          rowHeight={dirActions.length > 0 ? 'small' : 'medium'}
                          typeIcon={<FolderIcon />}
                          showExpiration={showExpiration}
                          elementName={
                            <Link
                              href={`/documents/${folder.id}`}
                              color="inherit"
                              onClick={(evt) => evt.stopPropagation()}
                              underline="hover"
                            >
                              {folder.name}
                            </Link>
                          }
                          purpose={<PurposeTag purpose={folder.purpose || 0} />}
                          expiresOn={moment()
                            .add(contentExpiresInDays, 'days')
                            .fromNow()}
                          actions={
                            <ActionSelectionButton
                              ariaLabel={intl.formatMessage(
                                messages.folderActions,
                                {
                                  folderName: folder.name,
                                }
                              )}
                              actions={dirActions}
                            />
                          }
                        />
                      );
                    }
                  )}
                  {sortBy(fetched.content?.documents ?? [], 'name').map(
                    (document) => {
                      const documentActions = getDocumentActions(document);
                      return (
                        <DocumentListRow
                          key={document.id}
                          defaultAction={() =>
                            openInNewTab(`${document.url}?disposition=inline`)
                          }
                          rowHeight={
                            documentActions.length > 0 ? 'small' : 'medium'
                          }
                          typeIcon={<InsertDriveFileIcon />}
                          showExpiration={showExpiration}
                          elementName={
                            <Link
                              href={`${document.url}?disposition=inline`}
                              target="_blank"
                              rel="noreferrer"
                              color="inherit"
                              onClick={(evt) => evt.stopPropagation()}
                              underline="hover"
                            >
                              {document.name}
                            </Link>
                          }
                          createdOn={moment(document.createdOn).fromNow()}
                          expiresOn={moment(document.createdOn)
                            .add(contentExpiresInDays, 'days')
                            .fromNow()}
                          author={document.author}
                          actions={
                            <ActionSelectionButton
                              ariaLabel={intl.formatMessage(
                                messages.documentActions,
                                {
                                  documentName: document.name,
                                }
                              )}
                              actions={documentActions}
                            />
                          }
                        />
                      );
                    }
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <ListItem>
              <Typography color="textSecondary">
                <FormattedMessage {...messages.noDocumentsYet} />
              </Typography>
            </ListItem>
          ))}
      </ContentView>
      <ConfirmationModal
        open={Boolean(folderToDelete)}
        title={
          folderToDelete ? (
            <FormattedMessage
              {...messages.deleteFolderConfirmTitle}
              values={{
                folderName: folderToDelete.name,
              }}
            />
          ) : (
            ''
          )
        }
        text={
          fullFolderToDelete?.content?.documents?.length &&
          fullFolderToDelete.content.documents.length > 0 ? (
            <FormattedMessage {...messages.deleteNonEmptyFolderConfirmText} />
          ) : (
            <FormattedMessage {...messages.deleteFolderConfirmText} />
          )
        }
        confirmLabel={
          <FormattedMessage {...messages.deleteFolderConfirmDelete} />
        }
        cancelLabel={
          <FormattedMessage {...messages.deleteFolderConfirmCancel} />
        }
        onCancel={() => setFolderToDelete(null)}
        onConfirm={() => {
          if (!folderToDelete?.id) {
            return;
          }
          return deleteFolderMutation.mutate({
            folderId: folderToDelete.id,
            recursive: true,
          });
        }}
      />
      <ConfirmationModal
        open={Boolean(documentToDelete)}
        title={
          documentToDelete ? (
            <FormattedMessage
              {...messages.deleteDocumentConfirmTitle}
              values={{
                documentName: documentToDelete.name,
              }}
            />
          ) : (
            ''
          )
        }
        text={<FormattedMessage {...messages.deleteDocumentConfirmText} />}
        confirmLabel={
          <FormattedMessage {...messages.deleteDocumentConfirmDelete} />
        }
        cancelLabel={
          <FormattedMessage {...messages.deleteDocumentConfirmCancel} />
        }
        onCancel={() => setDocumentToDelete(null)}
        onConfirm={() => {
          if (!documentToDelete?.id) {
            return;
          }
          return deleteDocumentMutation.mutate({
            documentId: documentToDelete.id,
          });
        }}
      />
      <RenameDocumentDialog
        open={Boolean(folderToRename)}
        action={(newName: string) => {
          if (!folderToRename?.id) {
            return;
          }
          return renameFolderMutation.mutate({
            folderId: folderToRename.id,
            newName,
          });
        }}
        onClose={() => setFolderToRename(null)}
        dialogTitle={intl.formatMessage(messages.renameFolderDialogTitle)}
        oldDocumentName={folderToRename?.name ?? null}
      />
      <RenameDocumentDialog
        open={Boolean(documentToRename)}
        action={(newName: string) => {
          if (!documentToRename?.id) {
            return;
          }
          return renameDocumentMutation.mutate({
            documentId: documentToRename.id,
            newName,
          });
        }}
        onClose={() => setDocumentToRename(null)}
        dialogTitle={intl.formatMessage(messages.renameDocumentDialogTitle)}
        oldDocumentName={documentToRename?.name ?? null}
      />
    </>
  );
};

export default DocumentList;
